// The in-memory pages twin (M7-02): Knowledge pages in Work's spaces, with the API's rules as the
// contracts and `@companyos/domain-work/pages` state them: a page nests under a live page of its
// own space and never under itself; siblings keep an order; `restricted` is inherited and a move
// never widens it; archiving moves the children up to the archived page's parent; every title or
// body change writes a version, a restore writes a new one; a task's body is a `task_body` page
// (`tasks/{id}/page`) that is never listed in its space. DOCS-KB: restore from the archive, a soft
// delete (`deleted_at`: gone from every list and read), the `kb` kind, the KB and meeting-notes
// templates, and `pages.readers` (empty here). The spaces, tasks and threads are the
// Work twin's (fake-work.ts `WorkBridge`). `fake-pages-contract.test.ts` holds it to the contracts.
import { blocksToText, markdownToBlocks } from "@companyos/domain-work/boards";
import { textToMarkdown } from "@companyos/domain-work/files";
import {
  ancestorsOf,
  breadcrumbsOf,
  buildTree,
  bySiblingOrder,
  descendantsOf,
  outlineOf,
  PAGE_TEMPLATES,
  pageLinksOf,
  placeAt,
  snippetOf,
} from "@companyos/domain-work/pages";
import { PAGE_UPLOAD_LIMITS } from "@companyos/schema/contracts";
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Handler, json, notFound, type Principal, page, problem, validation } from "./fake";
import type { FakeCompany } from "./fake-data";
import { DE_IDS, uid } from "./fake-data";
import { applyShares, type FakeShare, shareProblem } from "./fake-shares";

/** MULTI-SPACE-ACCESS: a doc is shared to edit, to comment, or (KB-UPLOAD-2) to read only. */
const DOC_LEVELS = ["editor", "viewer", "reader"] as const;
type DocLevel = (typeof DOC_LEVELS)[number];
const DOC_RANK: Record<DocLevel, number> = { reader: 1, viewer: 2, editor: 3 };

import type { WorkBridge } from "./fake-work";

type PageDetail = OperationOutput<"pages.get">;
type PageSummary = OperationOutput<"pages.list">["data"][number];
type PageVersion = OperationOutput<"pages.version">;
type Stored = Omit<
  PageDetail,
  "outline" | "breadcrumbs" | "children" | "task_id" | "can_edit" | "can_comment" | "can_manage"
> & {
  task_id: string | null;
};
type FakeMember = OperationOutput<"pages.members.list">["data"][number] & { page_id: string };
/**
 * MULTI-SPACE-ACCESS: `shares` are the pages' shares into more spaces (`space_share`).
 * KB-UPLOAD-2: `members` are the people and seats a page is shared with directly.
 */
type State = {
  pages: Stored[];
  versions: PageVersion[];
  shares: FakeShare[];
  members: FakeMember[];
};

export type PagesDeps = {
  now: () => Date;
  companies: FakeCompany[];
  work: () => WorkBridge | null;
};

const KINDS = ["page", "sop", "policy", "kb", "vision", "journal", "decision", "strategy"] as const;
const TEMPLATE_KEYS = ["sop", "policy", "kb", "meeting"] as const;
type TemplateKey = (typeof TEMPLATE_KEYS)[number];
const isTemplate = (v: unknown): v is TemplateKey =>
  (TEMPLATE_KEYS as readonly unknown[]).includes(v);
/** DOCS-KB seed authors: Jordan Lee (Acme's person) and Riley (Acme's recruiting DE). */
const JORDAN = uid(0x1000, 0xa11);
const RILEY = DE_IDS.riley;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const conflict = (detail: string) => problem(409, "conflict", "Conflict", detail);
/** UPLOAD-PRIVATE-FIRST: how long the twin takes to "read" an upload's text. */
const FAKE_READ_MS = 1500;
/** UPLOAD-ORIGINAL: a doc's original file is its managers' (the API's 403). */
const notManager = () =>
  problem(
    403,
    "forbidden",
    "Forbidden",
    "the original file is managed by the doc's maker, its space's owners or an admin",
  );
const sameDoc = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** RECALL-KB-LINKS (the twin's knowledge base): `[[Title]]` links and `#tags` in a doc's text. */
const wikiLinks = (text: string): string[] =>
  [...text.matchAll(/\[\[([^\]|#]+)(?:[|#][^\]]*)?\]\]/g)].map((m) => (m[1] as string).trim());
const tagsOf = (text: string): string[] =>
  [
    ...new Set([...text.matchAll(/(?<![\w#])#([A-Za-z][\w/-]*)/g)].map((m) => m[1] as string)),
  ].sort();
const INJECTION = /ignore (all |any )?(previous|prior) instructions/i;
const RELATED_OFF = {
  state: "off" as const,
  memory: "off" as const,
  backlinks: [],
  mentions: [],
  unresolved: [],
  tags: [],
  versions: null,
  review: "waiting" as const,
};

/** Client viewers read publications, not work (05): no page is theirs. */
const reader = (p: Principal | null): p is Principal =>
  p?.roles.some((r) => r !== "client_viewer") === true;

export function createPagesFake(deps: PagesDeps): {
  handlers: Partial<Record<RouteId, Handler>>;
  /** `spaces/{id}/sidebar`'s `pages`: live space pages in sibling order. */
  sidebarPages: (companyId: string, spaceId: string) => PageDetail["children"];
  /** M7-04: an accepted promotion's page (its id), or the refusal. */
  promote: (
    p: Principal,
    input: { space_id: string; title: string; kind: Stored["kind"]; markdown: string; ref: string },
  ) => string | Response;
  /** A task made with a body: its page holding that text; false when it cannot be made. */
  setTaskBody: (
    p: Principal,
    taskId: string,
    given: { body?: unknown[]; markdown?: string },
  ) => boolean;
  pagesOf: (
    company: FakeCompany,
  ) => readonly { id: string; title: string; kind: string; version: number; space_id: string }[];
} {
  const states = new Map<string, State>();
  let seq = 0x100;
  const nextId = () => uid(0x7200, ++seq);
  const stamp = () => deps.now().toISOString();

  const membershipOf = (p: Principal) =>
    p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id ?? null;
  const actorOf = (p: Principal | null) =>
    !p
      ? { actor_kind: "system" as const, actor_membership_id: null }
      : p.operator && !membershipOf(p)
        ? { actor_kind: "agency_user" as const, actor_membership_id: null }
        : { actor_kind: "person" as const, actor_membership_id: membershipOf(p) };

  function stateOf(company: FakeCompany): State {
    let s = states.get(company.id);
    if (s) return s;
    s = { pages: [], versions: [], shares: [], members: [] };
    states.set(company.id, s);
    seed(company, s);
    return s;
  }

  function insert(
    company: FakeCompany,
    s: State,
    p: Principal | null,
    input: {
      space_id: string;
      parent_page_id: string | null;
      kind: Stored["kind"];
      title: string;
      body: unknown[];
      restricted: boolean;
      icon?: string | null;
      position?: number | undefined;
      task_id?: string | null;
      /** A seeded author (membership id) instead of the caller. */
      by?: string;
    },
  ): Stored {
    const at = stamp();
    const id = nextId();
    const threadId = nextId();
    deps.work()?.addThread(company.id, threadId, id);
    const siblings = live(s).filter(
      (x) => x.space_id === input.space_id && x.parent_page_id === input.parent_page_id,
    );
    const order = placeAt(
      siblings.sort(bySiblingOrder).map((x) => x.id),
      id,
      input.position,
    );
    const actor = input.by
      ? { actor_kind: "person" as const, actor_membership_id: input.by }
      : actorOf(p);
    const row: Stored = {
      company_id: company.id,
      id,
      space_id: input.space_id,
      parent_page_id: input.parent_page_id,
      kind: input.kind,
      title: input.title,
      icon: input.icon ?? null,
      body: input.body as Stored["body"],
      body_text: blocksToText(input.body as never),
      version: 1,
      position: 0,
      restricted: input.restricted,
      read_only: false,
      promoted_from_memory_ref: null,
      thread_id: threadId,
      created_by_membership_id: actor.actor_membership_id,
      archived_at: null,
      deleted_at: null,
      ...actor,
      created_at: at,
      updated_at: at,
      task_id: input.task_id ?? null,
    };
    s.pages.push(row);
    renumber(s, order);
    writeVersion(s, row, actor, null);
    return row;
  }

  function seed(company: FakeCompany, s: State) {
    const spaces = deps.work()?.spacesOf(company.id) ?? [];
    const first = spaces.find((x) => x.name === "Recruiting") ?? spaces[0];
    if (!first) return;
    const md = (text: string) => markdownToBlocks(text);
    const acme = first.name === "Recruiting";
    /** DOCS-KB: seeded docs carry an author and an age, so the Docs list has owners and order. */
    const aged = (row: Stored, hours: number, by?: string) => {
      const at = new Date(deps.now().getTime() - hours * 3_600_000).toISOString();
      row.created_at = at;
      row.updated_at = at;
      if (by && acme) {
        row.created_by_membership_id = by;
        row.actor_kind = "person";
        row.actor_membership_id = by;
      }
      return row;
    };
    const sop = aged(
      insert(company, s, null, {
        space_id: first.id,
        parent_page_id: null,
        kind: "sop",
        title: "Screening calls",
        // RECALL-KB-LINKS: what memory reads back: a link nothing answers, a plain mention, a tag
        body: md(
          `${PAGE_TEMPLATES.sop.markdown}\n\nNext step: the Phone screen script, then [[Offer letters]]. #hiring #sop`,
        ),
        restricted: false,
      }),
      2,
      JORDAN,
    );
    aged(
      insert(company, s, null, {
        space_id: first.id,
        parent_page_id: sop.id,
        kind: "page",
        title: "Phone screen script",
        body: md(
          "## Opening\nIntroduce yourself and the role.\n\n## Questions\n- Why this role?\n- Notice period?",
        ),
        restricted: false,
      }),
      5,
      RILEY,
    );
    aged(
      insert(company, s, null, {
        space_id: first.id,
        parent_page_id: null,
        kind: "policy",
        title: "Salary bands",
        body: md(PAGE_TEMPLATES.policy.markdown),
        restricted: true,
      }),
      30,
      JORDAN,
    );
    // DOCS-KB: SOPs, policies and KB articles in Acme's other spaces, by different authors.
    const ops = spaces.find((x) => x.name === "Operations");
    const client = spaces.find((x) => x.name === "Client: Northwind");
    const more: [string | undefined, Stored["kind"], string, string, number, string][] = [
      [
        ops?.id,
        "sop",
        "Office opening",
        "## Steps\n1. Unlock and disarm\n2. Lights and coffee",
        26,
        JORDAN,
      ],
      [
        ops?.id,
        "policy",
        "Expenses",
        "## Policy statement\nReceipts within 30 days.\n\n## Exceptions\nThe owner approves.",
        72,
        JORDAN,
      ],
      [
        ops?.id,
        "kb",
        "Wi-Fi and printers",
        "## Summary\nGuest network: acme-guest.\n\n## Details\nThe printer is on the 2nd floor.",
        8,
        RILEY,
      ],
      [
        client?.id,
        "kb",
        "Northwind hiring profile",
        "## Summary\nNorthwind hires senior engineers in Berlin and Lisbon.",
        1,
        RILEY,
      ],
      [client?.id, "page", "Kickoff meeting notes", PAGE_TEMPLATES.meeting.markdown, 50, JORDAN],
    ];
    for (const [spaceId, kind, title, body, hours, by] of more)
      if (spaceId)
        aged(
          insert(company, s, null, {
            space_id: spaceId,
            parent_page_id: null,
            kind,
            title,
            body: md(body),
            restricted: false,
          }),
          hours,
          by,
        );
  }

  /** The tree rules' shape of the rows (camelCase parents). */
  const nodes = (s: State) =>
    s.pages.map((x) => ({ id: x.id, parentPageId: x.parent_page_id, title: x.title }));
  const nodeMap = (s: State) => new Map(nodes(s).map((n) => [n.id, n]));

  const live = (s: State) =>
    s.pages.filter((x) => !x.archived_at && !x.deleted_at && x.kind !== "task_body");

  function renumber(s: State, order: string[]) {
    order.forEach((id, i) => {
      const x = s.pages.find((r) => r.id === id);
      if (x) x.position = i;
    });
  }

  function writeVersion(
    s: State,
    row: Stored,
    actor: ReturnType<typeof actorOf>,
    restoredFrom: number | null,
  ) {
    s.versions.push({
      company_id: row.company_id,
      page_id: row.id,
      version: row.version,
      title: row.title,
      body: row.body,
      body_text: row.body_text,
      author_membership_id: actor.actor_membership_id,
      author_kind: actor.actor_kind,
      restored_from_version: restoredFrom,
      created_at: stamp(),
    });
  }

  const summary = (x: Stored): PageSummary => ({
    id: x.id,
    space_id: x.space_id,
    parent_page_id: x.parent_page_id,
    kind: x.kind,
    title: x.title,
    icon: x.icon,
    position: x.position,
    restricted: x.restricted,
    version: x.version,
    created_by_membership_id: x.created_by_membership_id,
    archived_at: x.archived_at,
    created_at: x.created_at,
    updated_at: x.updated_at,
    ...(uploads.has(x.id)
      ? {
          upload: {
            mime: uploads.get(x.id)?.view.mime as string,
            status: uploads.get(x.id)?.view.status as UploadView["status"],
          },
        }
      : {}),
  });

  // KB-PDF-UPLOAD: an uploaded page's document. The twin reads text files (txt, md); a PDF or
  // DOCX is kept but not read here (`failed`, saying so): extraction is the API's.
  type UploadView = NonNullable<PageDetail["upload"]>;
  const uploads = new Map<
    string,
    {
      view: UploadView;
      bytes: Uint8Array;
      markdown: string | null;
      /** UPLOAD-ORIGINAL: the earlier originals' bytes, by revision. */
      older?: Map<number, Uint8Array>;
    }
  >();

  const spaceOf = (p: Principal, id: string) =>
    deps
      .work()
      ?.spacesOf(p.company.id)
      ?.find((x) => x.id === id) ?? null;

  /** RECALL-KB-LINKS: the space's live docs the caller may view, as its knowledge base holds them. */
  /**
   * A version the injection scan holds (`flag`): memory keeps it out of the live note until an
   * admin clears it, so its words make no link, mention or tag.
   */
  const held = (v: { body_text: string }) => INJECTION.test(v.body_text);
  /** The version memory serves for a doc: the newest one not held; null when none is. */
  const liveVersion = (p: Principal, d: Stored) => {
    const mine = stateOf(p.company)
      .versions.filter((v) => v.page_id === d.id)
      .sort((a, b) => b.version - a.version);
    return mine.length === 0
      ? { version: d.version, body_text: d.body_text }
      : mine.find((v) => !held(v));
  };
  /**
   * The docs a space's knowledge base holds: HOMED in that space (a doc shared into it is held
   * where it lives, so it is neither counted nor linked here), live, not restricted, and read from
   * the live version, so a held edit changes nothing memory says.
   */
  const kbDocs = (p: Principal, spaceId: string): Stored[] =>
    stateOf(p.company)
      .pages.filter(
        (d) =>
          d.space_id === spaceId &&
          d.kind !== "task_body" &&
          !d.archived_at &&
          !d.deleted_at &&
          !d.restricted, // a restricted doc is never sent to memory
      )
      .flatMap((d) => {
        const v = liveVersion(p, d);
        return v ? [{ ...d, body_text: v.body_text }] : [];
      });

  /** KB-UPLOAD-2: owners, admins and the doc's maker manage it (the API's `managesPage`). */
  const manages = (p: Principal, x: Stored) =>
    p.roles.some((r) => r === "owner" || r === "admin") ||
    // SHARE-EVERYONE: Full access (manager) shares and manages like the maker.
    stateOf(p.company).members.some(
      (m) =>
        m.page_id === x.id &&
        m.level === "manager" &&
        m.principal_kind === "person" &&
        m.principal_id === membershipOf(p),
    ) ||
    (x.created_by_membership_id !== null && x.created_by_membership_id === membershipOf(p));

  /**
   * KB-UPLOAD-2: the twin's page role. Every non-client-viewer is its spaces' editor here; a
   * read-only doc gives them `reader` unless they manage it, and a direct share raises it.
   */
  function roleOf(p: Principal, x: Stored): DocLevel {
    const base: DocLevel = x.read_only && !manages(p, x) ? "reader" : "editor";
    const me = membershipOf(p);
    const s = stateOf(p.company);
    // SHARE-EVERYONE: a `company` entry counts for every member; the most permissive entry wins.
    const direct = s.members
      .filter(
        (m) =>
          m.page_id === x.id &&
          (m.principal_kind === "company" ||
            (m.principal_kind === "person" && m.principal_id === me)),
      )
      .map((m): DocLevel => (m.level === "manager" ? "editor" : m.level));
    // Space shares count as the API's do: the twin's caller is in every live space.
    const spaces = deps.work()?.spacesOf(p.company.id) ?? [];
    const shared = s.shares
      .filter(
        (sh) =>
          sh.object_type === "page" &&
          sh.object_id === x.id &&
          spaces.some((sp) => sp.id === sh.space_id && !sp.archived_at),
      )
      .map((sh): DocLevel | null =>
        sh.level === "owner" || sh.level === "editor"
          ? "editor"
          : x.restricted
            ? null
            : (sh.level as DocLevel),
      )
      .filter((l): l is DocLevel => l !== null);
    return [...direct, ...shared].reduce<DocLevel>(
      (best, l) => (DOC_RANK[l] > DOC_RANK[best] ? l : best),
      base,
    );
  }

  /** The API's `structure` writes (move, archive, delete): an editor who manages the doc. */
  const mayRestructure = (p: Principal, x: Stored) =>
    roleOf(p, x) === "editor" && (manages(p, x) || !x.read_only);
  const notYours = () =>
    problem(
      403,
      "forbidden",
      "Forbidden",
      "a read-only doc is moved, archived or deleted by its managers",
    );

  function detail(p: Principal, x: Stored): PageDetail {
    const s = stateOf(p.company);
    const byId = nodeMap(s);
    const { task_id, ...row } = x;
    const space = spaceOf(p, x.space_id);
    const writable = !x.archived_at && !space?.archived_at;
    const role = roleOf(p, x);
    return {
      ...row,
      outline: outlineOf(x.body),
      breadcrumbs: x.kind === "task_body" ? [] : breadcrumbsOf(byId, x.parent_page_id, () => true),
      children: live(s)
        .filter((c) => c.parent_page_id === x.id)
        .sort(bySiblingOrder)
        .map(summary),
      task_id,
      can_edit: writable && role === "editor",
      can_comment: writable && role !== "reader",
      can_manage: writable && x.kind !== "task_body" && (manages(p, x) || !x.read_only),
      ...(uploads.has(x.id) ? { upload: uploads.get(x.id)?.view as UploadView } : {}),
    };
  }

  /**
   * UPLOAD-PRIVATE-FIRST: a private doc (its maker's personal space) is hidden from everyone but
   * its owner unless shared with them (a direct share, or a space share: the twin's caller is in
   * every shared space). The API's `loadPage` / `pageViewSql` rule.
   */
  function hiddenFrom(p: Principal, x: Stored): boolean {
    const home = spaceOf(p, x.space_id);
    if (home?.kind !== "personal") return false;
    const me = membershipOf(p);
    if (home.owner_membership_id === me) return false;
    const s = stateOf(p.company);
    const direct = s.members.some(
      (m) =>
        m.page_id === x.id &&
        (m.principal_kind === "company" ||
          (m.principal_kind === "person" && m.principal_id === me)),
    );
    const shared = s.shares.some((sh) => sh.object_type === "page" && sh.object_id === x.id);
    return !direct && !shared;
  }

  /**
   * WIKI-LINKS-GRAPH: a doc's links, resolved: `target` is a live doc the caller may open,
   * `hidden` a live doc it may not (never named); neither is a link no live doc answers.
   */
  function outLinksOf(
    p: Principal,
    x: Stored,
  ): { id: string | null; label: string; target: string | null; hidden: boolean }[] {
    const all = stateOf(p.company).pages.filter((d) => !d.deleted_at && d.kind !== "task_body");
    return pageLinksOf((x.body ?? []) as unknown[], x.id).map((l) => {
      const t = l.target_page_id
        ? all.find((d) => d.id.toLowerCase() === l.target_page_id)
        : all.find((d) => d.id !== x.id && d.title.toLowerCase() === l.target_title.toLowerCase());
      if (!t) return { id: l.target_page_id, label: l.target_title, target: null, hidden: false };
      if (hiddenFrom(p, t)) return { id: null, label: "", target: null, hidden: true };
      return { id: t.id, label: l.target_title, target: t.id, hidden: false };
    });
  }

  /** WIKI-LINKS-GRAPH: the docs the caller may open and the links between them. */
  function linkGraphOf(p: Principal) {
    const open = new Map(
      stateOf(p.company)
        .pages.filter((d) => !d.deleted_at && d.kind !== "task_body" && !hiddenFrom(p, d))
        .map((d) => [d.id, d] as const),
    );
    const out = new Map<string, string[]>();
    const edges: { source: string; target: string }[] = [];
    for (const d of open.values()) {
      const targets = [
        ...new Set(
          outLinksOf(p, d)
            .map((l) => l.target)
            .filter((t): t is string => !!t && open.has(t) && t !== d.id),
        ),
      ];
      out.set(d.id, targets);
      for (const t of targets) edges.push({ source: d.id, target: t });
    }
    return { open, out, edges };
  }

  const graphNode = (d: Stored, edges: { source: string; target: string }[]) => ({
    id: d.id,
    title: d.title,
    space_id: d.space_id,
    kind: d.kind as string | null,
    degree: edges.filter((e) => e.source === d.id || e.target === d.id).length,
    missing: false,
  });

  /**
   * UPLOAD-PRIVATE-FIRST: the API reads an upload's text in the background (the worker's
   * `upload.extract`); the twin does so a moment later. Text files read; a PDF or Word file
   * fails here (the twin has no reader), keeping the original.
   */
  function readLater(x: Stored, replace = false) {
    setTimeout(() => {
      const u = uploads.get(x.id);
      if (!u || u.view.status !== "processing") return;
      const at = stamp();
      const textual = u.view.mime.startsWith("text/");
      const markdown = textual ? textToMarkdown(new TextDecoder().decode(u.bytes)) : null;
      // UPLOAD-ORIGINAL: Replace file and Re-extract write the file's text as a new version.
      if (markdown && (replace || !x.body_text.trim())) {
        x.body = markdownToBlocks(markdown) as Stored["body"];
        x.body_text = blocksToText(x.body as never);
        x.version += 1;
        x.updated_at = at;
      }
      u.markdown = markdown || null;
      u.view = {
        ...u.view,
        status: !textual ? "failed" : markdown ? "done" : "no_text",
        error: textual ? null : "the in-memory API does not read PDF or Word files",
        markdown_chars: markdown ? markdown.length : null,
        extracted_at: at,
        updated_at: at,
      };
    }, FAKE_READ_MS);
  }

  /** UPLOAD-PRIVATE-FIRST: the caller's personal space (made once), a private doc's home. */
  function personalSpaceOf(p: Principal): string | null {
    const me = membershipOf(p);
    const spaces = deps.work()?.spacesOf(p.company.id);
    if (!me || !spaces) return null;
    const have = spaces.find(
      (x) => x.kind === "personal" && x.owner_membership_id === me && !x.archived_at,
    );
    if (have) return have.id;
    const at = stamp();
    const id = uid(0x7e50, spaces.length + 1);
    spaces.push({
      company_id: p.company.id,
      id,
      name: "My docs",
      kind: "personal",
      team_id: null,
      owner_membership_id: me,
      icon: null,
      description: null,
      is_private: true,
      settings: {},
      position: Date.parse(at),
      archived_at: null,
      created_at: at,
      updated_at: at,
      ...actorOf(null),
    } as unknown as (typeof spaces)[number]);
    return id;
  }

  function pageIn(p: Principal, id: string | undefined): Stored | null {
    if (!id || !UUID.test(id)) return null;
    const found = stateOf(p.company).pages.find((r) => r.id === id && !r.deleted_at) ?? null;
    const x = found && hiddenFrom(p, found) ? null : found;
    if (x?.kind === "task_body" && x.task_id && !deps.work()?.taskOf(p.company.id, x.task_id)) {
      // The API archives a deleted task's body page with it (`archiveTaskBodyPages`); the twin
      // does so the first time the page is read after its task went.
      const at = stamp();
      x.archived_at ??= at;
      x.updated_at = at;
      x.version += 1;
      x.task_id = null;
    }
    return x;
  }

  function precondition(headers: Headers, version: number): Response | null {
    const raw = headers.get("if-match");
    if (!raw) return problem(428, "precondition_required", "If-Match required");
    if (raw.replaceAll('"', "") !== String(version))
      return conflict(`The page is at version ${version}; reload and retry.`);
    return null;
  }

  /** `body` / `body_markdown` / `template`: at most one; the blocks it gives, or a problem. */
  function bodyOf(b: Record<string, unknown>): unknown[] | undefined | Response {
    const given = ["body", "body_markdown", "template"].filter((k) => b[k] !== undefined);
    if (given.length > 1) return validation(`give one of ${given.join(", ")}`);
    if (Array.isArray(b.body)) return b.body;
    if (typeof b.body_markdown === "string") return markdownToBlocks(b.body_markdown);
    if (isTemplate(b.template)) return markdownToBlocks(PAGE_TEMPLATES[b.template].markdown);
    if (b.template !== undefined) return validation("template must be sop, policy, kb or meeting");
    return undefined;
  }

  /** `restricted` cascades down (05: a page under a restricted page is restricted). */
  function restrictBelow(s: State, id: string) {
    for (const d of descendantsOf(nodes(s), id)) {
      const x = s.pages.find((r) => r.id === d);
      if (x) x.restricted = true;
    }
  }

  const sidebarPages = (companyId: string, spaceId: string) => {
    const company = deps.companies.find((c) => c.id === companyId);
    if (!company) return [];
    // Depth first, each level in sibling order: a parent always comes before its children.
    const rows = live(stateOf(company)).filter(
      (x) => x.space_id === spaceId && x.kind !== "board_note",
    );
    const tree = buildTree(rows.map((x) => ({ ...x, parentPageId: x.parent_page_id })));
    type Node = (typeof tree)[number];
    const flat = (list: Node[]): Node[] => list.flatMap((n) => [n, ...flat(n.children)]);
    return flat(tree).map((n) => summary(n));
  };

  const handlers: Partial<Record<RouteId, Handler>> = {
    "pages.list": ({ principal, url }) => {
      if (!principal) return notFound();
      if (!reader(principal)) return json(page([], url));
      const q = url.searchParams;
      const s = stateOf(principal.company);
      const rows = s.pages
        .filter(
          (x) =>
            x.kind !== "task_body" &&
            // W1-FIXES: a board's note lives on its board, not in the Docs list
            (x.kind !== "board_note" || q.get("kind") === "board_note") &&
            !x.deleted_at &&
            !hiddenFrom(principal, x) &&
            (q.get("include_archived") === "true" || !x.archived_at) &&
            (!q.get("space_id") || x.space_id === q.get("space_id")) &&
            (!q.get("parent_page_id") || x.parent_page_id === q.get("parent_page_id")) &&
            (!q.get("kind") || x.kind === q.get("kind")),
        )
        .sort(bySiblingOrder)
        .map(summary);
      return json(page(rows, url));
    },
    "pages.create": ({ principal, body }) => {
      if (!reader(principal)) return notFound();
      const b = (body ?? {}) as Record<string, unknown>;
      const s = stateOf(principal.company);
      const title = typeof b.title === "string" ? b.title.trim() : "";
      if (!title) return validation("title is required");
      const space = typeof b.space_id === "string" ? spaceOf(principal, b.space_id) : null;
      if (!space) return validation("space_id: no such space");
      if (space.archived_at) return conflict("The space is archived.");
      const kind = (b.kind ??
        (isTemplate(b.template) ? PAGE_TEMPLATES[b.template].kind : "page")) as Stored["kind"];
      if (!(KINDS as readonly string[]).includes(kind))
        return validation("kind: not a page kind you can create");
      const blocks = bodyOf(b);
      if (blocks instanceof Response) return blocks;
      let parent: Stored | null = null;
      if (typeof b.parent_page_id === "string") {
        parent = pageIn(principal, b.parent_page_id);
        if (!parent || parent.kind === "task_body")
          return validation("parent_page_id: no such page");
        if (parent.space_id !== space.id)
          return validation("parent_page_id: the parent page is in another space");
        if (parent.archived_at) return validation("parent_page_id: the parent page is archived");
      }
      const spaces = deps.work()?.spacesOf(principal.company.id) ?? [];
      const badShare =
        b.shares === undefined ? null : shareProblem(b.shares, space.id, spaces, DOC_LEVELS);
      if (badShare) return validation(badShare);
      const row = insert(principal.company, s, principal, {
        space_id: space.id,
        parent_page_id: parent?.id ?? null,
        kind,
        title,
        body: blocks ?? [],
        restricted: b.restricted === true || parent?.restricted === true,
        icon: typeof b.icon === "string" ? b.icon : null,
        position: typeof b.position === "number" ? b.position : undefined,
      });
      if (Array.isArray(b.shares) && b.shares.length)
        applyShares(s.shares, "page", row.id, b.shares as FakeShare[], stamp());
      return json(detail(principal, row), 201);
    },
    "pages.upload": ({ principal, body }) => {
      if (!reader(principal)) return notFound();
      const b = (body ?? {}) as {
        __files?: Record<string, { bytes: Uint8Array; name: string; type: string }>;
        space_id?: string;
        kind?: string;
        title?: string;
        read_only?: string;
        shares?: string;
      };
      const part = b.__files?.file;
      if (!part) return validation("file: a `file` part is required");
      if (part.bytes.byteLength === 0) return validation("file: the file is empty");
      if (part.bytes.byteLength > PAGE_UPLOAD_LIMITS.maxBytes)
        return problem(
          413,
          "payload_too_large",
          "Payload too large",
          `File too large: the Knowledge base takes documents up to ${PAGE_UPLOAD_LIMITS.maxMb} MB`,
        );
      const ext = part.name.split(".").pop()?.toLowerCase() ?? "";
      const mime =
        ext === "pdf"
          ? "application/pdf"
          : ext === "docx"
            ? PAGE_UPLOAD_LIMITS.mimes[1]
            : ext === "md" || ext === "markdown"
              ? "text/markdown"
              : ext === "txt"
                ? "text/plain"
                : null;
      if (!mime) return validation("file: Upload a PDF, Word (.docx) or text (.txt, .md) file");
      // UPLOAD-PRIVATE-FIRST: no space_id → private, in the uploader's personal space.
      const homeId = typeof b.space_id === "string" ? b.space_id : personalSpaceOf(principal);
      const space = homeId ? spaceOf(principal, homeId) : null;
      if (!space) return validation("space_id: no such space");
      if (space.archived_at) return conflict("The space is archived.");
      // KB-UPLOAD-2: the space shares ride along as a JSON part (people and seats: members.set).
      let shares: FakeShare[] = [];
      if (b.shares) {
        try {
          shares = JSON.parse(b.shares) as FakeShare[];
        } catch {
          return validation("shares: JSON expected");
        }
        const spaces = deps.work()?.spacesOf(principal.company.id) ?? [];
        const bad = shareProblem(shares, space.id, spaces, DOC_LEVELS);
        if (bad) return validation(bad);
      }
      const row = insert(principal.company, stateOf(principal.company), principal, {
        space_id: space.id,
        parent_page_id: null,
        kind: ((b.kind as Stored["kind"]) ?? "kb") as Stored["kind"],
        title:
          b.title?.trim() || part.name.replace(/\.[A-Za-z0-9]{1,10}$/, "") || "Untitled document",
        body: [],
        restricted: false,
      });
      if (b.read_only === "true") row.read_only = true;
      if (shares.length)
        applyShares(stateOf(principal.company).shares, "page", row.id, shares, stamp());
      const at = stamp();
      uploads.set(row.id, {
        bytes: part.bytes,
        markdown: null,
        view: {
          page_id: row.id,
          file_name: part.name,
          mime,
          bytes: part.bytes.byteLength,
          status: "processing",
          error: null,
          markdown_chars: null,
          source_pages: null,
          truncated: false,
          extracted_at: null,
          revision: 1,
          original_uploaded_at: at,
          original_removed_at: null,
          previous_versions: [],
          uploader_membership_id: row.created_by_membership_id,
          actor_kind: row.actor_kind,
          actor_membership_id: row.actor_membership_id,
          created_at: at,
          updated_at: at,
        },
      });
      readLater(row);
      return json(detail(principal, row), 201);
    },
    "pages.uploadRetry": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const x = pageIn(principal, params.id);
      const u = x ? uploads.get(x.id) : undefined;
      if (!x || !u) return notFound();
      if (u.view.status === "done") return conflict("the text was extracted already");
      u.view = { ...u.view, status: "processing", error: null, updated_at: stamp() };
      readLater(x);
      return json(detail(principal, x));
    },
    "pages.uploadContent": ({ principal, params, url }) => {
      if (!reader(principal)) return notFound();
      const x = pageIn(principal, params.id);
      const u = x ? uploads.get(x.id) : undefined;
      if (!x || !u) return notFound();
      if (url.searchParams.get("as") === "markdown") {
        if (u.markdown === null) return notFound();
        return new Response(u.markdown, { headers: { "content-type": "text/markdown" } });
      }
      const rev = Number(url.searchParams.get("revision") ?? u.view.revision);
      if (rev !== u.view.revision) {
        const old = u.older?.get(rev);
        const v = u.view.previous_versions?.find((p) => p.revision === rev);
        if (!old || !v) return notFound();
        return new Response(old.slice().buffer as ArrayBuffer, {
          headers: { "content-type": v.mime },
        });
      }
      if (u.view.original_removed_at) return notFound();
      return new Response(u.bytes.slice().buffer as ArrayBuffer, {
        headers: { "content-type": u.view.mime },
      });
    },
    // UPLOAD-ORIGINAL: the doc's managers manage its original file (the API's rules).
    "pages.uploadReplace": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      const x = pageIn(principal, params.id);
      const u = x ? uploads.get(x.id) : undefined;
      if (!x || !u) return notFound();
      if (!manages(principal, x)) return notManager();
      const part = (body as { __files?: Record<string, { bytes: Uint8Array; name: string }> })
        ?.__files?.file;
      if (!part || part.bytes.byteLength === 0)
        return validation("file: a `file` part is required");
      const at = stamp();
      const keep = !u.view.original_removed_at;
      u.older ??= new Map();
      if (keep) u.older.set(u.view.revision, u.bytes);
      const mime = /\.pdf$/i.test(part.name)
        ? "application/pdf"
        : /\.docx$/i.test(part.name)
          ? PAGE_UPLOAD_LIMITS.mimes[1]
          : "text/markdown";
      u.view = {
        ...u.view,
        previous_versions: [
          ...(u.view.previous_versions ?? []),
          ...(keep
            ? [
                {
                  revision: u.view.revision,
                  file_name: u.view.file_name,
                  mime: u.view.mime,
                  bytes: u.view.bytes,
                  uploaded_at: u.view.original_uploaded_at ?? u.view.created_at,
                  replaced_at: at,
                },
              ]
            : []),
        ],
        revision: u.view.revision + 1,
        file_name: part.name,
        mime,
        bytes: part.bytes.byteLength,
        original_uploaded_at: at,
        original_removed_at: null,
        status: "processing",
        error: null,
        updated_at: at,
      };
      u.bytes = part.bytes;
      readLater(x, true);
      return json(detail(principal, x));
    },
    "pages.uploadReextract": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const x = pageIn(principal, params.id);
      const u = x ? uploads.get(x.id) : undefined;
      if (!x || !u) return notFound();
      if (!manages(principal, x)) return notManager();
      if (u.view.original_removed_at) return conflict("the original was removed: upload it again");
      u.view = { ...u.view, status: "processing", error: null, updated_at: stamp() };
      readLater(x, true);
      return json(detail(principal, x));
    },
    "pages.uploadRemoveOriginal": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const x = pageIn(principal, params.id);
      const u = x ? uploads.get(x.id) : undefined;
      if (!x || !u) return notFound();
      if (!manages(principal, x)) return notManager();
      if (u.view.original_removed_at) return conflict("the original was removed already");
      const at = stamp();
      u.older = new Map();
      u.view = {
        ...u.view,
        original_removed_at: at,
        previous_versions: [],
        ...(u.view.status === "processing"
          ? { status: "failed" as const, error: "the original was removed" }
          : {}),
        updated_at: at,
      };
      return json(detail(principal, x));
    },
    "pages.uploadDetach": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const x = pageIn(principal, params.id);
      if (!x || !uploads.has(x.id)) return notFound();
      if (!manages(principal, x)) return notManager();
      uploads.delete(x.id);
      return json(detail(principal, x));
    },
    // MULTI-SPACE-ACCESS: shares of live pages into live spaces, and a page's set.
    "pages.shares.list": ({ principal, url }) => {
      if (!principal) return notFound();
      if (!reader(principal)) return json({ data: [] });
      const s = stateOf(principal.company);
      const only = url.searchParams.get("object_id");
      const spaces = deps.work()?.spacesOf(principal.company.id) ?? [];
      const data = s.shares.filter(
        (x) =>
          (!only || x.object_id === only) &&
          s.pages.some((p) => p.id === x.object_id && !p.deleted_at) &&
          spaces.some((sp) => sp.id === x.space_id && !sp.archived_at),
      );
      return json({ data });
    },
    "pages.shares.set": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      const row = pageIn(principal, params.id ?? "");
      if (!row || row.deleted_at) return notFound();
      if (row.kind === "task_body" || row.kind === "board_note")
        return validation("id: this page belongs to its task or board");
      // KB-UPLOAD-2: a read-only doc is shared by its managers only.
      if (row.read_only ? !manages(principal, row) : roleOf(principal, row) !== "editor")
        return problem(403, "forbidden", "Forbidden", "you cannot share this doc");
      const shares = (body as { shares?: unknown } | null)?.shares;
      const spaces = deps.work()?.spacesOf(principal.company.id) ?? [];
      const bad = shareProblem(shares, row.space_id, spaces, DOC_LEVELS);
      if (bad) return validation(bad);
      const s = stateOf(principal.company);
      return json({ data: applyShares(s.shares, "page", row.id, shares as FakeShare[], stamp()) });
    },
    // KB-UPLOAD-2: the people and seats (DEs) a page is shared with directly.
    "pages.members.list": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const row = pageIn(principal, params.id ?? "");
      if (!row) return notFound();
      const data = stateOf(principal.company)
        .members.filter((m) => m.page_id === row.id)
        .map(({ page_id: _, ...m }) => m);
      return json({ data });
    },
    "pages.members.set": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      const row = pageIn(principal, params.id ?? "");
      if (!row || row.deleted_at) return notFound();
      if (row.kind === "task_body" || row.kind === "board_note")
        return validation("id: this page belongs to its task or board");
      if (row.read_only ? !manages(principal, row) : roleOf(principal, row) !== "editor")
        return problem(
          403,
          "forbidden",
          "Forbidden",
          "a doc is shared by its space's owners, its maker or an admin",
        );
      const home = spaceOf(principal, row.space_id);
      // UPLOAD-PRIVATE-FIRST: a private doc is shared by its owner only.
      if (home?.kind === "personal" && home.owner_membership_id !== membershipOf(principal))
        return problem(403, "forbidden", "Forbidden", "a private doc is shared by its owner");
      if (row.archived_at || home?.archived_at) return conflict("The page is archived.");
      const input = (body as { members?: unknown } | null)?.members;
      if (!Array.isArray(input)) return validation("members: a list");
      if (input.length > 50) return validation("members: at most 50");
      const seen = new Set<string>();
      for (const m of input as {
        principal_kind?: unknown;
        principal_id?: unknown;
        level?: unknown;
      }[]) {
        if (
          m.principal_kind !== "person" &&
          m.principal_kind !== "seat" &&
          m.principal_kind !== "company"
        )
          return validation("members: principal_kind is person, seat or company");
        if (typeof m.principal_id !== "string" || !UUID.test(m.principal_id))
          return validation("members: principal_id is an id");
        if (!([...DOC_LEVELS, "manager"] as readonly unknown[]).includes(m.level))
          return validation(`members: level is ${[...DOC_LEVELS, "manager"].join(", ")}`);
        // SHARE-EVERYONE: everyone can view or edit, never manage.
        if (m.principal_kind === "company" && m.level === "manager")
          return validation("members: everyone can view or edit, not manage");
        const k = `${m.principal_kind}:${m.principal_id}`;
        if (seen.has(k)) return validation("members: named twice");
        seen.add(k);
      }
      const s = stateOf(principal.company);
      const before = s.members.filter((m) => m.page_id === row.id);
      s.members = s.members.filter((m) => m.page_id !== row.id);
      const at = stamp();
      for (const m of input as Omit<FakeMember, "page_id" | "created_at">[]) {
        const had = before.find(
          (x) => x.principal_kind === m.principal_kind && x.principal_id === m.principal_id,
        );
        s.members.push({
          page_id: row.id,
          principal_kind: m.principal_kind,
          principal_id: m.principal_id,
          level: m.level,
          created_at: had?.created_at ?? at,
        });
      }
      const data = s.members.filter((m) => m.page_id === row.id).map(({ page_id: _, ...m }) => m);
      return json({ data });
    },
    "pages.search": ({ principal, body }) => {
      if (!principal) return notFound();
      const b = (body ?? {}) as {
        query?: string;
        space_id?: string;
        kinds?: string[];
        limit?: number;
      };
      const term = (b.query ?? "").trim();
      if (!term) return validation("query is required");
      if (!reader(principal)) return json({ data: [], next_cursor: null, prev_cursor: null });
      const lower = term.toLowerCase();
      const hits = live(stateOf(principal.company))
        .filter(
          (x) =>
            (!b.space_id || x.space_id === b.space_id) &&
            !hiddenFrom(principal, x) &&
            (!b.kinds?.length || b.kinds.includes(x.kind)) &&
            (x.title.toLowerCase().includes(lower) || x.body_text.toLowerCase().includes(lower)),
        )
        .map((x) => ({ x, title: x.title.toLowerCase().includes(lower) }))
        .sort(
          (a, c) =>
            Number(c.title) - Number(a.title) || c.x.updated_at.localeCompare(a.x.updated_at),
        )
        .slice(0, Math.min(50, b.limit ?? 20))
        .map(({ x }) => ({
          ...summary(x),
          space_name: spaceOf(principal, x.space_id)?.name ?? "",
          snippet: snippetOf(x.body_text, term),
        }));
      return json({ data: hits, next_cursor: null, prev_cursor: null });
    },
    "pages.get": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const x = pageIn(principal, params.id);
      return x ? json(detail(principal, x)) : notFound();
    },
    "pages.update": ({ principal, params, body, headers }) => {
      if (!reader(principal)) return notFound();
      const x = pageIn(principal, params.id);
      if (!x) return notFound();
      if (x.archived_at) return conflict("The page is archived.");
      const pre = precondition(headers, x.version);
      if (pre) return pre;
      const b = (body ?? {}) as Record<string, unknown>;
      // KB-UPLOAD-2: a read-only doc is its managers' (and editor shares') to change.
      if (roleOf(principal, x) !== "editor")
        return problem(
          403,
          "forbidden",
          "Forbidden",
          "this doc is read only: ask its owner for edit access",
        );
      // The API's rule: an open doc is its space's editors' to manage; a read-only one its managers'.
      const managing =
        (typeof b.read_only === "boolean" && b.read_only !== x.read_only) ||
        (x.read_only && b.kind !== undefined && b.kind !== x.kind);
      if (managing && x.read_only && !manages(principal, x))
        return problem(
          403,
          "forbidden",
          "Forbidden",
          "only the doc's maker, its space's owners or an admin change what it is",
        );
      if (b.body !== undefined && b.body_markdown !== undefined)
        return validation("give body or body_markdown, not both");
      if (
        x.kind === "task_body" &&
        (b.kind !== undefined || b.restricted !== undefined || b.read_only !== undefined)
      )
        return validation("A task's page follows its task");
      if (b.kind !== undefined && !(KINDS as readonly string[]).includes(b.kind as string))
        return validation("kind: not a page kind you can set");
      const s = stateOf(principal.company);
      const parent = x.parent_page_id ? s.pages.find((r) => r.id === x.parent_page_id) : null;
      if (b.restricted === false && parent?.restricted)
        return validation("restricted: the parent page is restricted");
      const blocks =
        b.body !== undefined
          ? (b.body as unknown[])
          : typeof b.body_markdown === "string"
            ? markdownToBlocks(b.body_markdown)
            : undefined;
      const title = typeof b.title === "string" ? b.title.trim() : undefined;
      const changed = {
        title: title !== undefined && title !== x.title,
        body: blocks !== undefined && !sameDoc(blocks, x.body),
        icon: b.icon !== undefined && b.icon !== x.icon,
        kind: b.kind !== undefined && b.kind !== x.kind,
        restricted: typeof b.restricted === "boolean" && b.restricted !== x.restricted,
        read_only: typeof b.read_only === "boolean" && b.read_only !== x.read_only,
      };
      if (!Object.values(changed).some(Boolean)) return json(detail(principal, x));
      if (changed.title) x.title = title as string;
      if (changed.body) {
        x.body = blocks as Stored["body"];
        x.body_text = blocksToText(blocks as never);
      }
      if (changed.icon) x.icon = (b.icon as string | null) ?? null;
      if (changed.kind) x.kind = b.kind as Stored["kind"];
      if (changed.read_only) x.read_only = b.read_only as boolean;
      if (changed.restricted) {
        x.restricted = b.restricted as boolean;
        if (x.restricted) restrictBelow(s, x.id);
      }
      x.version += 1;
      x.updated_at = stamp();
      const actor = actorOf(principal);
      Object.assign(x, actor);
      if (changed.title || changed.body) writeVersion(s, x, actor, null);
      return json(detail(principal, x));
    },
    "pages.move": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      const x = pageIn(principal, params.id);
      if (!x) return notFound();
      if (x.archived_at) return conflict("The page is archived.");
      if (x.kind === "task_body") return conflict("A task's page moves with its task");
      if (!mayRestructure(principal, x)) return notYours();
      const b = (body ?? {}) as {
        space_id?: string;
        parent_page_id?: string | null;
        position?: number;
      };
      const s = stateOf(principal.company);
      const spaceId = b.space_id ?? x.space_id;
      const space = spaceOf(principal, spaceId);
      if (!space) return validation("space_id: no such space");
      if (space.archived_at) return conflict("The space is archived.");
      const parentId =
        b.parent_page_id !== undefined
          ? b.parent_page_id
          : spaceId !== x.space_id
            ? null
            : x.parent_page_id;
      const byId = nodeMap(s);
      let parent: Stored | null = null;
      if (parentId) {
        parent = pageIn(principal, parentId);
        if (!parent || parent.kind === "task_body")
          return validation("parent_page_id: no such page");
        if (parent.id === x.id || ancestorsOf(byId, parent.id).includes(x.id))
          return validation("A page cannot move under itself or one of its own pages");
        if (parent.space_id !== spaceId)
          return validation("parent_page_id: the parent page is in another space");
        if (parent.archived_at) return validation("parent_page_id: the parent page is archived");
      }
      const siblings = live(s)
        .filter((r) => r.space_id === spaceId && r.parent_page_id === (parentId ?? null))
        .sort(bySiblingOrder)
        .map((r) => r.id);
      const order = placeAt(siblings, x.id, b.position);
      const same =
        spaceId === x.space_id &&
        (parentId ?? null) === x.parent_page_id &&
        order.indexOf(x.id) === siblings.indexOf(x.id);
      if (same) return json(detail(principal, x));
      const below = descendantsOf(nodes(s), x.id);
      x.space_id = spaceId;
      x.parent_page_id = parentId ?? null;
      for (const d of below) {
        const r = s.pages.find((y) => y.id === d);
        if (r) r.space_id = spaceId;
      }
      if (parent?.restricted && !x.restricted) {
        x.restricted = true;
        restrictBelow(s, x.id);
      }
      renumber(s, order);
      x.version += 1;
      x.updated_at = stamp();
      return json(detail(principal, x));
    },
    "pages.archive": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const x = pageIn(principal, params.id);
      if (!x) return notFound();
      if (x.archived_at) return json(detail(principal, x));
      if (!mayRestructure(principal, x)) return notYours();
      const s = stateOf(principal.company);
      for (const c of s.pages.filter((r) => r.parent_page_id === x.id))
        c.parent_page_id = x.parent_page_id;
      x.archived_at = stamp();
      x.updated_at = x.archived_at;
      x.version += 1;
      return json(detail(principal, x));
    },
    // DOCS-KB: back from the archive under its old parent when that is live, else at the top of
    // its space; last among its new siblings either way.
    "pages.unarchive": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const x = pageIn(principal, params.id);
      if (!x) return notFound();
      if (x.kind === "task_body") return conflict("A task's page follows its task");
      if (!x.archived_at) return json(detail(principal, x));
      const s = stateOf(principal.company);
      const parent = x.parent_page_id
        ? live(s).find((r) => r.id === x.parent_page_id && r.space_id === x.space_id)
        : undefined;
      x.parent_page_id = parent?.id ?? null;
      const siblings = live(s)
        .filter((r) => r.space_id === x.space_id && r.parent_page_id === x.parent_page_id)
        .sort(bySiblingOrder)
        .map((r) => r.id);
      x.archived_at = null;
      renumber(s, [...siblings, x.id]);
      x.updated_at = stamp();
      x.version += 1;
      return json(detail(principal, x));
    },
    // DOCS-KB: a soft delete by an editor; the page leaves every list and its children float up.
    "pages.delete": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const x = pageIn(principal, params.id);
      if (!x) return notFound();
      if (x.kind === "task_body") return conflict("A task's page goes with its task");
      if (!x.archived_at && !mayRestructure(principal, x)) return notYours();
      if (spaceOf(principal, x.space_id)?.archived_at)
        return problem(403, "forbidden", "Only editors can delete", "The space is archived.");
      const s = stateOf(principal.company);
      for (const c of s.pages.filter((r) => r.parent_page_id === x.id))
        c.parent_page_id = x.parent_page_id;
      x.deleted_at = stamp();
      x.updated_at = x.deleted_at;
      return new Response(null, { status: 204 });
    },
    // ACTIONS: the deleted pages of live spaces, and one brought back — live in its old place
    // when it was live, archived when it was archived; its floated children stay where they went.
    "pages.deleted": ({ principal, url }) => {
      if (!principal) return notFound();
      if (!reader(principal)) return json(page([], url));
      const spaceId = url.searchParams.get("space_id");
      const rows = stateOf(principal.company)
        .pages.filter(
          (x) =>
            x.kind !== "task_body" &&
            x.deleted_at &&
            !spaceOf(principal, x.space_id)?.archived_at &&
            (!spaceId || x.space_id === spaceId),
        )
        .sort((a, b) => a.id.localeCompare(b.id))
        .map((x) => ({ ...summary(x), deleted_at: x.deleted_at as string }));
      return json(page(rows, url));
    },
    "pages.undelete": ({ principal, params }) => {
      if (!reader(principal) || !params.id) return notFound();
      const s = stateOf(principal.company);
      const x = s.pages.find((r) => r.id === params.id && r.deleted_at && r.kind !== "task_body");
      if (!x) return notFound();
      if (spaceOf(principal, x.space_id)?.archived_at) return conflict("The space is archived.");
      x.deleted_at = null;
      if (!x.archived_at) {
        const parent = x.parent_page_id
          ? live(s).find((r) => r.id === x.parent_page_id && r.space_id === x.space_id)
          : undefined;
        x.parent_page_id = parent?.id ?? null;
        const siblings = live(s)
          .filter(
            (r) =>
              r.id !== x.id && r.space_id === x.space_id && r.parent_page_id === x.parent_page_id,
          )
          .sort(bySiblingOrder)
          .map((r) => r.id);
        renumber(s, [...siblings, x.id]);
      }
      x.updated_at = stamp();
      x.version += 1;
      return json(detail(principal, x));
    },
    // The agents that read the page: the knowledge twin (fake-knowledge.ts) serves the real list.
    "pages.readers": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      return pageIn(principal, params.id) ? json({ data: [] }) : notFound();
    },
    // DOCS-ALWAYS-IN-MEMORY: the twin's worker syncs at once: an open space page is in memory.
    "pages.memory": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const x = pageIn(principal, params.id);
      if (!x) return notFound();
      const space = spaceOf(principal, x.space_id);
      const reason =
        x.kind === "task_body"
          ? null
          : x.archived_at
            ? "archived"
            : space?.kind === "personal"
              ? "private"
              : x.restricted
                ? "restricted"
                : null;
      const out =
        reason || x.kind === "task_body"
          ? { state: "not_in_memory" as const, reason }
          : { state: "in_memory" as const, reason: null };
      return json({
        ...out,
        spaces: principal.manager
          ? reason || !space
            ? []
            : [
                {
                  space_id: space.id,
                  name: space.name,
                  via: "home" as const,
                  sync_enabled: true,
                  current: true,
                  synced_at: x.updated_at,
                  last_error: null,
                },
              ]
          : null,
      });
    },
    // RECALL-KB-LINKS, DOC-LINKS: backlinks, links that point nowhere and `#tags` come from the
    // docs themselves (any doc but a task's body, memory on or off; the twin's links are
    // `[[Title]]`, the API's are doc addresses); memory adds plain mentions of a title and the
    // admin's versions when the space's knowledge base holds the doc. Review waits.
    "pages.related": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const x = pageIn(principal, params.id);
      if (!x) return notFound();
      if (x.kind === "task_body") return json(RELATED_OFF);
      const lc = x.title.toLowerCase();
      const link = (d: Stored, text: string) => ({
        page_id: d.id,
        title: d.title,
        snippet: text.replace(/\s+/g, " ").trim().slice(0, 160),
      });
      const live = stateOf(principal.company).pages.filter(
        (d) => !d.deleted_at && d.kind !== "task_body",
      );
      const linksHere = (d: Stored) => wikiLinks(d.body_text).some((t) => t.toLowerCase() === lc);
      const backlinks = live
        .filter((d) => d.id !== x.id && !d.archived_at && !hiddenFrom(principal, d) && linksHere(d))
        .map((d) => link(d, d.body_text));
      const own = {
        state: "ready" as const,
        backlinks,
        unresolved: [
          ...new Set(
            wikiLinks(x.body_text).filter(
              (t) => !live.some((d) => d.title.toLowerCase() === t.toLowerCase()),
            ),
          ),
        ],
        tags: tagsOf(x.body_text),
      };
      const room =
        spaceOf(principal, x.space_id)?.kind === "personal" ? [] : kbDocs(principal, x.space_id);
      if (!room.some((d) => d.id === x.id))
        return json({ ...RELATED_OFF, ...own, memory: "off" as const });
      const others = room.filter((d) => d.id !== x.id);
      return json({
        ...own,
        memory: "ready" as const,
        mentions: others
          .filter(
            (d) =>
              d.body_text
                .replace(/\[\[[^\]]*\]\]/g, "")
                .toLowerCase()
                .includes(lc) && !linksHere(d),
          )
          .map((d) => link(d, d.body_text)),
        versions: principal.manager
          ? stateOf(principal.company)
              .versions.filter((v) => v.page_id === x.id)
              .sort((a, c) => c.version - a.version)
              .map((v) => ({
                version: v.version,
                created_at: v.created_at,
                origin: "install",
                held: held(v),
                // the live version is the newest one that is not held
                current: v.version === liveVersion(principal, x)?.version,
                doc_version: held(v) ? null : v.version,
              }))
          : null,
        review: "waiting" as const,
      });
    },
    // WIKI-LINKS-GRAPH: links out (live titles; missing ones), backlinks, unlinked mentions and
    // the graphs, read from each body with the API's parser (`pageLinksOf`); `[[Title]]` resolves
    // by title here at read time (the API at save). Hidden docs are never named.
    "pages.links": ({ principal, params, url }) => {
      if (!reader(principal)) return notFound();
      const x = pageIn(principal, params.id);
      if (!x) return notFound();
      if (x.kind === "task_body") return json({ outgoing: [], backlinks: [], mentions: null });
      const g = linkGraphOf(principal);
      type Out = { page_id: string | null; title: string; state: "ok" | "missing" };
      const outgoing = outLinksOf(principal, x).flatMap((l): Out[] => {
        if (l.hidden) return [];
        if (l.target) {
          const t = g.open.get(l.target);
          return t ? [{ page_id: t.id, title: t.title, state: "ok" as const }] : [];
        }
        return [{ page_id: l.id, title: l.label || "Untitled", state: "missing" as const }];
      });
      const backlinks = [...g.open.values()]
        .filter((d) => d.id !== x.id && (g.out.get(d.id) ?? []).includes(x.id))
        .map((d) => ({ page_id: d.id, title: d.title, snippet: d.body_text.slice(0, 160) }));
      const lc = x.title.toLowerCase();
      const mentions =
        url.searchParams.get("mentions") === "true" && lc.length >= 3
          ? [...g.open.values()]
              .filter(
                (d) =>
                  d.id !== x.id &&
                  !d.archived_at &&
                  d.body_text.toLowerCase().includes(lc) &&
                  !(g.out.get(d.id) ?? []).includes(x.id),
              )
              .map((d) => ({
                page_id: d.id,
                title: d.title,
                snippet: snippetOf(d.body_text, x.title) ?? "",
              }))
          : null;
      return json({ outgoing, backlinks, mentions });
    },
    "pages.graph": ({ principal, params, url }) => {
      if (!reader(principal)) return notFound();
      const x = pageIn(principal, params.id);
      if (!x) return notFound();
      if (x.kind === "task_body") return json({ nodes: [], edges: [], truncated: false });
      const g = linkGraphOf(principal);
      const depth = url.searchParams.get("depth") === "2" ? 2 : 1;
      const inside = new Set([x.id]);
      let frontier = [x.id];
      for (let hop = 0; hop < depth; hop++) {
        const next: string[] = [];
        for (const e of g.edges)
          for (const [a, b] of [
            [e.source, e.target],
            [e.target, e.source],
          ] as const)
            if (frontier.includes(a) && !inside.has(b)) {
              inside.add(b);
              next.push(b);
            }
        frontier = next;
      }
      const edges = g.edges.filter((e) => inside.has(e.source) && inside.has(e.target));
      const nodes: {
        id: string;
        title: string;
        space_id: string | null;
        kind: string | null;
        degree: number;
        missing: boolean;
      }[] = [...inside].map((id) => graphNode(g.open.get(id) ?? x, edges));
      for (const l of outLinksOf(principal, x))
        if (!l.target && !l.hidden) {
          const id = `missing:${l.id ?? l.label.toLowerCase()}`;
          edges.push({ source: x.id, target: id });
          nodes.push({ id, title: l.label, space_id: null, kind: null, degree: 1, missing: true });
        }
      return json({ nodes, edges, truncated: false });
    },
    "pages.graphAll": ({ principal, url }) => {
      if (!reader(principal)) return notFound();
      const g = linkGraphOf(principal);
      const space = url.searchParams.get("space_id");
      const tag = url.searchParams.get("tag")?.toLowerCase();
      const orphans = url.searchParams.get("orphans") ?? "show";
      const limit = Math.min(2000, Number(url.searchParams.get("limit") ?? 2000) || 2000);
      const docs = [...g.open.values()].filter(
        (d) =>
          !d.archived_at &&
          (!space || d.space_id === space) &&
          (!tag || tagsOf(d.body_text).includes(tag)),
      );
      const ids = new Set(docs.map((d) => d.id));
      const among = g.edges.filter((e) => ids.has(e.source) && ids.has(e.target));
      const linked = new Set(among.flatMap((e) => [e.source, e.target]));
      const kept = docs.filter((d) =>
        orphans === "only" ? !linked.has(d.id) : orphans === "hide" ? linked.has(d.id) : true,
      );
      const shown = kept.slice(0, limit);
      const keep = new Set(shown.map((d) => d.id));
      const edges = among.filter((e) => keep.has(e.source) && keep.has(e.target));
      return json({
        nodes: shown.map((d) => graphNode(d, edges)),
        edges,
        truncated: kept.length > limit,
      });
    },
    "pages.memoryReview": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      if (!pageIn(principal, params.id)) return notFound();
      if (!principal.manager)
        return problem(403, "forbidden", "Forbidden", "Only admins review memory.");
      return json({ result: "waiting" as const });
    },
    "spaces.docsMemory": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const sp = spaceOf(principal, params.id ?? "");
      if (!sp || sp.kind === "personal") return notFound();
      const room = kbDocs(principal, sp.id);
      const tags = new Map<string, string[]>();
      for (const d of room)
        for (const t of tagsOf(d.body_text)) tags.set(t, [...(tags.get(t) ?? []), d.id]);
      const missing = new Map<string, { page_id: string; title: string }[]>();
      for (const d of room)
        for (const t of new Set(wikiLinks(d.body_text)))
          if (!room.some((r) => r.title.toLowerCase() === t.toLowerCase()))
            missing.set(t, [...(missing.get(t) ?? []), { page_id: d.id, title: d.title }]);
      return json({
        state: room.length ? ("ready" as const) : ("off" as const),
        docs: room.length,
        parts: room.length * 3,
        synced_at: stamp(),
        tags: [...tags].map(([tag, page_ids]) => ({ tag, page_ids, more: false })),
        unresolved: [...missing].map(([target, pages]) => ({ target, pages })),
      });
    },
    "pages.children": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const x = pageIn(principal, params.id);
      if (!x) return notFound();
      return json({ data: detail(principal, x).children });
    },
    "pages.versions": ({ principal, params, url }) => {
      if (!reader(principal)) return notFound();
      const x = pageIn(principal, params.id);
      if (!x) return notFound();
      const rows = stateOf(principal.company)
        .versions.filter((v) => v.page_id === x.id)
        .sort((a, c) => c.version - a.version)
        .map(({ body: _b, body_text: _t, ...v }) => v);
      return json(page(rows, url));
    },
    "pages.version": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const x = pageIn(principal, params.id);
      if (!x) return notFound();
      const v = stateOf(principal.company).versions.find(
        (r) => r.page_id === x.id && r.version === Number(params.version),
      );
      return v ? json(v) : notFound();
    },
    "pages.restore": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      const x = pageIn(principal, params.id);
      if (!x) return notFound();
      if (x.archived_at) return conflict("The page is archived.");
      const want = (body as { version?: number } | null)?.version;
      const s = stateOf(principal.company);
      const v = s.versions.find((r) => r.page_id === x.id && r.version === want);
      if (!v) return validation("version: no such version of this page");
      x.title = v.title;
      x.body = v.body;
      x.body_text = v.body_text;
      x.version += 1;
      x.updated_at = stamp();
      const actor = actorOf(principal);
      Object.assign(x, actor);
      writeVersion(s, x, actor, v.version);
      return json(detail(principal, x));
    },
    "tasks.page": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const work = deps.work();
      const t = work?.taskOf(principal.company.id, params.id ?? "");
      if (!work || !t) return notFound();
      const s = stateOf(principal.company);
      const existing = t.page_id ? s.pages.find((x) => x.id === t.page_id) : null;
      if (existing) return json(detail(principal, existing));
      const board = work.boardOf(principal.company.id, t.board_id);
      if (!board) return conflict("The task's board is archived.");
      const row = insert(principal.company, s, principal, {
        space_id: board.space_id,
        parent_page_id: null,
        kind: "task_body",
        title: t.title,
        body: [],
        restricted: t.restricted,
        task_id: t.id,
      });
      t.page_id = row.id;
      return json(detail(principal, row));
    },
  };

  /**
   * M7-04 (fake-memory.ts): an accepted promotion's page, made as `pages.create` makes one and
   * naming the Recall proposal it came from. Returns its id, or the refusal `pages.create` gives.
   */
  function promote(
    p: Principal,
    input: { space_id: string; title: string; kind: Stored["kind"]; markdown: string; ref: string },
  ): string | Response {
    if (!reader(p)) return notFound();
    const space = spaceOf(p, input.space_id);
    if (!space) return validation("space_id: no such space");
    if (space.archived_at) return conflict("The space is archived.");
    const s = stateOf(p.company);
    const row = insert(p.company, s, p, {
      space_id: space.id,
      parent_page_id: null,
      kind: input.kind,
      title: input.title,
      body: markdownToBlocks(input.markdown) as unknown[],
      restricted: false,
    });
    row.promoted_from_memory_ref = input.ref;
    return row.id;
  }

  /**
   * A task made with a `body` / `body_markdown` (the real `tasks.create`): its page, as
   * `tasks.page` makes it, holding that text. False when the task or its board is gone.
   */
  function setTaskBody(
    p: Principal,
    taskId: string,
    given: { body?: unknown[]; markdown?: string },
  ): boolean {
    const work = deps.work();
    const t = work?.taskOf(p.company.id, taskId);
    if (!work || !t || t.page_id) return false;
    const board = work.boardOf(p.company.id, t.board_id);
    if (!board) return false;
    const body = given.body ?? markdownToBlocks(given.markdown ?? "");
    const row = insert(p.company, stateOf(p.company), p, {
      space_id: board.space_id,
      parent_page_id: null,
      kind: "task_body",
      title: t.title,
      body: body as unknown[],
      restricted: t.restricted,
      task_id: t.id,
    });
    t.page_id = row.id;
    return true;
  }

  /** BIZ-PLAYBOOKS: the company's stored pages (the seed's doc_update proposal reads one). */
  const pagesOf = (company: FakeCompany): readonly Stored[] => stateOf(company).pages;

  return { handlers, sidebarPages, promote, setTaskBody, pagesOf };
}
